import { Container, Graphics, Ticker } from 'pixi.js';
import { COLORS, DESIGN_W, DESIGN_H } from '../app/theme';
import { makeText } from './widgets';
import { animate, easeOutCubic, linear, type TweenHandle } from '../app/anim';

/**
 * Lightweight, self-disposing visual effects for the table — the "game-engine feel" juice
 * (win confetti, lose flash, the "+amount" bet float, the active-turn glow ring). All are built
 * on Graphics + the shared-ticker tween core; each cleans itself up so nothing leaks across rounds.
 */

const WIN_GREEN = 0x4ade80;
const CONFETTI_COLORS = [COLORS.gold, WIN_GREEN, COLORS.white, 0xffd700, 0xff8c42];

/** A floating, rising-and-fading text (e.g. "+۱۰۰۰" over the bet box on a raise). */
export function floatingText(layer: Container, text: string, x: number, y: number, color = WIN_GREEN): void {
  const t = makeText(text, { size: 32, weight: '700', align: 'center', fill: color });
  t.anchor.set(0.5);
  t.position.set(x, y);
  t.style.dropShadow = { color: 0x000000, alpha: 0.6, blur: 2, distance: 2, angle: Math.PI / 2 };
  layer.addChild(t);
  animate({
    duration: 1100,
    ease: easeOutCubic,
    bind: layer,
    onUpdate: (p) => {
      t.y = y - 70 * p;
      t.alpha = p < 0.6 ? 1 : 1 - (p - 0.6) / 0.4;
    },
    onComplete: () => {
      if (!t.destroyed) t.destroy();
    },
  });
}

/**
 * A burst of confetti particles falling from a point (win celebration). Particles get random
 * velocity + gravity + spin, fade near the end, and the whole batch self-destroys.
 */
export function confettiBurst(layer: Container, x = DESIGN_W / 2, y = DESIGN_H * 0.32, count = 26): void {
  const holder = new Container();
  holder.position.set(x, y);
  layer.addChild(holder);

  interface P {
    g: Graphics;
    vx: number;
    vy: number;
    spin: number;
  }
  const parts: P[] = [];
  for (let i = 0; i < count; i++) {
    const color = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
    const g = new Graphics().rect(-5, -3, 10, 6).fill({ color });
    const angle = (Math.PI * 2 * i) / count + (i % 3) * 0.3;
    const speed = 3 + (i % 5);
    parts.push({ g, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed - 4, spin: (i % 2 ? 1 : -1) * 0.2 });
    holder.addChild(g);
  }

  const ticker = Ticker.shared;
  let life = 0;
  const total = 1300;
  const step = (): void => {
    if (holder.destroyed) {
      ticker.remove(step);
      return;
    }
    const dt = ticker.deltaMS;
    life += dt;
    const f = dt / 16.67; // frame-normalized
    for (const p of parts) {
      p.vy += 0.22 * f; // gravity
      p.g.x += p.vx * f;
      p.g.y += p.vy * f;
      p.g.rotation += p.spin * f;
    }
    if (life > total * 0.6) holder.alpha = Math.max(0, 1 - (life - total * 0.6) / (total * 0.4));
    if (life >= total) {
      ticker.remove(step);
      if (!holder.destroyed) holder.destroy({ children: true });
    }
  };
  ticker.add(step);
}

/** A full-screen red flash that fades out (lose feedback). */
export function loseFlash(layer: Container): void {
  const g = new Graphics().rect(0, 0, DESIGN_W, DESIGN_H).fill({ color: COLORS.danger, alpha: 0.4 });
  layer.addChild(g);
  animate({
    duration: 600,
    bind: layer,
    onUpdate: (p) => {
      g.alpha = 0.4 * (1 - p);
    },
    onComplete: () => {
      if (!g.destroyed) g.destroy();
    },
  });
}

/**
 * A soft pulsing glow ring placed behind a node to mark the active turn. Returns a handle whose
 * `cancel()` stops the loop and removes the ring — call it when the turn changes / scene re-renders.
 */
export function glowRing(parent: Container, cx: number, cy: number, radius: number, color = COLORS.gold): TweenHandle {
  const ring = new Graphics().circle(cx, cy, radius).stroke({ width: 4, color, alpha: 0.9 });
  ring.alpha = 0;
  parent.addChildAt(ring, 0);
  const ticker = Ticker.shared;
  let t = 0;
  const step = (): void => {
    if (ring.destroyed) {
      ticker.remove(step);
      return;
    }
    t += ticker.deltaMS;
    const phase = (Math.sin(t / 380) + 1) / 2; // 0..1
    ring.alpha = 0.35 + phase * 0.5;
    ring.scale.set(0.96 + phase * 0.08);
  };
  // Keep the pulse centered while scaling.
  ring.pivot.set(cx, cy);
  ring.position.set(cx, cy);
  ticker.add(step);
  return {
    cancel() {
      ticker.remove(step);
      if (!ring.destroyed) ring.destroy();
    },
  };
}

export interface TurnRingOpts {
  cx: number;
  cy: number;
  radius: number;
  /** Seconds to deplete over (the turn's `turnNextTrigger`). <=0 draws a static full ring. */
  seconds: number;
  color?: number;
  trackColor?: number;
  width?: number;
  /** Fired each frame with the whole seconds remaining (to drive a numeric badge). */
  onTick?: (secondsLeft: number) => void;
  /** Fired once when the ring fully depletes (used to hand off to the reserve-time ring). */
  onComplete?: () => void;
}

/**
 * The active-turn timer ring: a faint full-circle track with a foreground arc that sweeps clockwise
 * from 12 o'clock and depletes over `seconds`, shifting to danger-red in the final quarter. Purely
 * cosmetic (the server is authoritative on timeouts). Returns a handle whose `cancel()` stops the
 * tween and removes both rings — call it on turn change / re-render. Replaces `glowRing` on the table.
 */
export function turnRing(parent: Container, opts: TurnRingOpts): TweenHandle {
  const { cx, cy, radius, seconds } = opts;
  const color = opts.color ?? WIN_GREEN;
  const trackColor = opts.trackColor ?? COLORS.panelBorder;
  const width = opts.width ?? 7;
  const start = -Math.PI / 2;

  // A recessed dark track (with a faint inner ring) so the bright depleting arc reads on any background.
  const track = new Graphics()
    .circle(cx, cy, radius)
    .stroke({ width: width + 4, color: COLORS.scrim, alpha: 0.5 })
    .circle(cx, cy, radius)
    .stroke({ width, color: trackColor, alpha: 0.45 });
  const glow = new Graphics(); // soft halo under the live arc
  const arc = new Graphics();
  parent.addChild(track, glow, arc);

  const draw = (frac: number): void => {
    arc.clear();
    glow.clear();
    if (frac <= 0) return;
    const col = frac < 0.25 ? COLORS.danger : color;
    const end = start + frac * Math.PI * 2;
    glow.arc(cx, cy, radius, start, end).stroke({ width: width + 7, color: col, alpha: 0.2, cap: 'round' });
    arc.arc(cx, cy, radius, start, end).stroke({ width, color: col, alpha: 1, cap: 'round' });
  };

  const dispose = (): void => {
    if (!track.destroyed) track.destroy();
    if (!glow.destroyed) glow.destroy();
    if (!arc.destroyed) arc.destroy();
  };

  draw(1);
  if (seconds <= 0) {
    opts.onTick?.(0);
    return { cancel: dispose };
  }

  opts.onTick?.(seconds);
  const tween = animate({
    duration: seconds * 1000,
    ease: linear,
    bind: arc,
    onUpdate: (p) => {
      const frac = 1 - p;
      draw(frac);
      opts.onTick?.(Math.max(0, Math.ceil(seconds * frac)));
    },
    onComplete: opts.onComplete,
  });
  return {
    cancel() {
      tween.cancel();
      dispose();
    },
  };
}
